import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useTranslation } from "react-i18next";
import { Landmark } from "lucide-react";

export default function ActualitesPropositions() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || "fr";

  const fetchProposals = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("proposals")
      .select("*, artwork:artwork_id(title, artist)")
      .in("status", ["pending", "paid"])
      .order("created_at", { ascending: false });

    if (error) {
      console.error(
        t("client.proposals.error.fetch", { error: error.message })
      );
    } else {
      setProposals(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProposals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancelProposal = async (proposal) => {
    if (proposal.status === "pending") {
      if (
        !window.confirm(t("client.proposals.confirm.delete"))
      )
        return;

      try {
        const { error } = await supabase
          .from("proposals")
          .delete()
          .eq("id", proposal.id);

        if (error) throw error;

        alert(t("client.proposals.alert.deleted"));
        fetchProposals();
      } catch (err) {
        console.error("Erreur suppression:", err);
        alert(t("client.proposals.alert.deleteError"));
      }
    }

    if (proposal.status === "paid") {
      if (
        !window.confirm(
          t("client.proposals.confirm.requestCancellation")
        )
      )
        return;

      try {
        const { error } = await supabase
          .from("proposals")
          .update({ cancellation_requested: true })
          .eq("id", proposal.id);

        if (error) throw error;

        alert(t("client.proposals.alert.cancellationRequested"));
        fetchProposals();
      } catch (err) {
        console.error("Erreur demande d'annulation:", err);
        alert(t("client.proposals.alert.cancellationError"));
      }
    }
  };

  if (loading) {
    return (
      <p className="text-sm text-gray-500">
        {t("client.proposals.loading")}
      </p>
    );
  }

  // ✅ Au lieu de masquer le bloc, on explique ce que c'est + empty state
  const hasAny = proposals.length > 0;


  return (
    <div className="rounded-2xl border border-[#e8e3d8] bg-white p-5">
      <div className="flex items-start gap-3 mb-4">
        <span
          className="flex-shrink-0 inline-flex items-center justify-center rounded-xl"
          style={{ width: 40, height: 40, background: "#f5f3ee" }}
          aria-hidden="true"
        >
          <Landmark className="w-5 h-5" strokeWidth={1.75} style={{ color: "#1a1a1a" }} />
        </span>
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {t("client.proposals.operational.title", {
              defaultValue: "Exhibition, loan & donation",
            })}
          </div>
          <div className="mt-1 text-sm text-gray-600">
            {t("client.proposals.operational.desc", {
              defaultValue:
                "Track requests you have sent to institutions for an artwork.",
            })}
          </div>
        </div>
      </div>

      {!hasAny ? (
        <div className="text-center text-sm text-gray-500 py-6">
          <div className="font-medium text-gray-700">
            {t("client.proposals.empty.title")}
          </div>
          <div className="mt-1">
            {t("client.proposals.empty.desc")}
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left border-collapse">
            <thead className="border-b border-gray-300">
              <tr className="text-gray-700">
                <th className="py-2 pr-4 font-medium">
                  {t("client.proposals.column.artwork")}
                </th>
                <th className="py-2 pr-4 font-medium">
                  {t("client.proposals.column.institution")}
                </th>
                <th className="py-2 pr-4 font-medium">
                  {t("client.proposals.column.type")}
                </th>
                <th className="py-2 pr-4 font-medium">
                  {t("client.proposals.column.date")}
                </th>
                <th className="py-2 pr-4 font-medium">
                  {t("client.proposals.column.action")}
                </th>
              </tr>
            </thead>
            <tbody>
              {proposals.map((proposal) => (
                <tr key={proposal.id} className="border-t border-gray-200">
                  <td className="py-2 pr-4">
                    <div className="font-medium">
                      {proposal.artwork?.title || "—"}
                    </div>
                    <div className="text-xs text-gray-500 italic">
                      {proposal.artwork?.artist ||
                        t("client.proposals.artwork.unknownArtist")}
                    </div>
                  </td>
                  <td className="py-2 pr-4">{proposal.institution}</td>
                  <td className="py-2 pr-4">
                    {proposal.proposalType === "auction"
                      ? t("client.proposals.type.auction")
                      : t("client.proposals.type.institution")}
                  </td>
                  <td className="py-2 pr-4">
                    {new Date(proposal.created_at).toLocaleDateString(locale)}
                  </td>
                  <td className="py-2 pr-4">
                    <button
                      onClick={() => handleCancelProposal(proposal)}
                      className="text-red-600 hover:underline text-sm btn-secondary-artsy"
                    >
                      {t("client.proposals.button.cancel")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}